import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { productTypes } from '../../../types/products';

import {
  SECTION_DETAIL,
  SECTION_SELECT,
  SECTION_SUBMITTED,
  PAYMENT_METHOD_BILLABLE,
  PAYMENT_METHOD_COMP_UNIT,
  PAYMENT_METHOD_COMP_CASH,
  SECTION_FINALIZE
} from '../constants';

import { isUSSalesOrg } from '../../../util/constants';

import { TranslatedCopy } from '../../../intl/TranslatedCopy';

import { Button, Currency, H2, Spinner } from '../../../components';

const MAX_TOTAL_UNITS = 50;
const MAX_TOTAL_DOLLARS = 10000;

const OrderReturnsStatusFinal = ({
  currentStep,
  section,
  returnAmount,
  currencyCode,
  totalQuantity,
  sectionProgress,
  submitting,
  valid,
  hasQuantityError,
  displayList,
  detailUpdate,
  isSaving,
  paymentMethod,
  salesOrg,
  currentAddress,
  submittedReturns
}) => {
  const getButtonCopy = currentSection => {
    switch (currentSection) {
      case SECTION_DETAIL:
        return <TranslatedCopy id='orderreturns.selectProducts' />;
      case SECTION_SELECT:
        return <TranslatedCopy id='orderreturns.finalizeReturn' />;
      default:
        return <TranslatedCopy id='orderreturns.submitReturn' />;
    }
  };

  const onButtonClick = () => {
    // Sending directly as Button onClick will result in error due to silent event-params.  Keep separate.
    sectionProgress();
  };

  const isCurrentReturnAboveLimit =
    isUSSalesOrg(salesOrg) &&
    ((paymentMethod === PAYMENT_METHOD_BILLABLE && returnAmount > MAX_TOTAL_DOLLARS) ||
      (paymentMethod === PAYMENT_METHOD_COMP_CASH && returnAmount > MAX_TOTAL_DOLLARS) ||
      (paymentMethod === PAYMENT_METHOD_COMP_UNIT && totalQuantity > MAX_TOTAL_UNITS));

  const combinedReturnsDollarAmount =
    submittedReturns?.objects?.reduce((acc, val) => acc + val?.creditedAmount || 0, 0) || 0;

  const combinedReturnsUnitAmount =
    submittedReturns?.objects?.productToReturn?.reduce((acc, val) => acc + val?.quantity || 0, 0) || 0;

  const isCombinedReturnsAboveLimit =
    isUSSalesOrg(salesOrg) &&
    (paymentMethod === PAYMENT_METHOD_COMP_UNIT
      ? combinedReturnsUnitAmount + totalQuantity > MAX_TOTAL_UNITS
      : combinedReturnsDollarAmount + returnAmount > MAX_TOTAL_DOLLARS);

  return (
    <div
      className={classnames('orderReturns__section_status prl6-sm pb6-sm', {
        'pt6-sm border-top': currentStep > 0
      })}
    >
      {currentStep > 1 && (
        <>
          <div className='flex-container row space-between'>
            <H2>
              <TranslatedCopy id='orderreturns.returnAmount' />*
            </H2>
            <span>
              <Currency value={returnAmount || '0'} type={currencyCode} />
            </span>
          </div>
        </>
      )}
      {section !== SECTION_SUBMITTED && (
        <Button
          type='button'
          data-desc='orderReturns__section_button'
          extraClassName='u-full-width mt3-sm orderReturns__section_button ncss-btn-primary-dark'
          onClick={onButtonClick}
          disabled={
            detailUpdate ||
            submitting ||
            !valid ||
            (section === SECTION_SELECT &&
              (!displayList ||
                displayList.length === 0 ||
                totalQuantity < 1 ||
                hasQuantityError ||
                isCurrentReturnAboveLimit ||
                isCombinedReturnsAboveLimit)) ||
            (section === SECTION_FINALIZE && !currentAddress) ||
            isSaving
          }
        >
          {isSaving && (
            <div className='flex-container center'>
              <Spinner />
            </div>
          )}
          {submitting ? <TranslatedCopy id='global.submitting' /> : getButtonCopy(section)}
        </Button>
      )}
      {section === SECTION_SELECT && isCurrentReturnAboveLimit && !isCombinedReturnsAboveLimit && (
        <div className='text-color-error mt4-sm ta-sm-c'>
          <TranslatedCopy id='orderreturns.exceedMaximum' />
        </div>
      )}
      {section === SECTION_SELECT && isCombinedReturnsAboveLimit && (
        <div className='text-color-error mt4-sm ta-sm-c'>
          <TranslatedCopy id='orderreturns.existingReturnsExceedMaxThreshold' />
        </div>
      )}
    </div>
  );
};

OrderReturnsStatusFinal.propTypes = {
  currentStep: PropTypes.number.isRequired,
  section: PropTypes.string.isRequired,
  returnAmount: PropTypes.number.isRequired,
  currencyCode: PropTypes.string,
  totalQuantity: PropTypes.number.isRequired,
  sectionProgress: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  valid: PropTypes.bool.isRequired,
  hasQuantityError: PropTypes.bool.isRequired,
  displayList: PropTypes.arrayOf(productTypes),
  detailUpdate: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  paymentMethod: PropTypes.string.isRequired,
  salesOrg: PropTypes.string.isRequired,
  currentAddress: PropTypes.string.isRequired,
  submittedReturns: PropTypes.arrayOf(PropTypes.object).isRequired
};

OrderReturnsStatusFinal.defaultProps = {
  displayList: [],
  currencyCode: null
};

export default OrderReturnsStatusFinal;
